import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { storeReset, storeCheckCode } from '../../../redux/Slices/storeAuthSlice';
import styles from './StoreOTP.module.css';

const StoreOTP = () => {
    const [otp, setOtp] = useState(['', '', '', '']);
    const [shake, setShake] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [phone, setPhone] = useState('');
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isActivation, setIsActivation] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loading, error, otpVerified } = useSelector((state) => state.storeAuth);

    useEffect(() => {
        if (location.state?.phone) {
            setPhone(location.state.phone);
            setIsForgotPassword(location.state.isForgotPassword || false);
            setIsActivation(location.state.isActivation || false);
        } else {
            navigate('/store-register');
        }
    }, [location.state, navigate]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    useEffect(() => {
        if (otpVerified) {
            if (isForgotPassword) {
                toast.success('تم التحقق من الكود بنجاح', {
                    autoClose: 1500,
                    onClose: () => {
                        navigate('/store-change-password', { state: { phone: phone } });
                    }
                });
            } else if (isActivation) {
                toast.success('تم تفعيل الحساب بنجاح', {
                    autoClose: 1500,
                    onClose: () => {
                        navigate('/login');
                    }
                });
            } else {
                toast.success('تم التحقق من الكود بنجاح', {
                    autoClose: 1500,
                    onClose: () => {
                        navigate('/login');
                    }
                });
            }
        }
    }, [otpVerified, isForgotPassword, isActivation, navigate, phone]);

    useEffect(() => {
        if (error) {
            setShake(true);
            toast.error(error, {
                autoClose: 1500
            });
            setTimeout(() => setShake(false), 500);
        }
    }, [error]);

    const handleOtpChange = (index, value) => {
        if (value.length > 1) return; // منع إدخال أكثر من رقم واحد
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // الانتقال للحقل التالي تلقائياً
        if (value && index < 3) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // الانتقال للحقل السابق عند الضغط على Backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
        const newOtp = [...otp];
        for (let i = 0; i < pastedData.length; i++) {
            newOtp[i] = pastedData[i];
        }
        setOtp(newOtp);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const otpCode = otp.join('');
        if (otpCode.length !== 4) {
            setShake(true);
            toast.error('يرجى إدخال الكود المكون من 4 أرقام');
            setTimeout(() => setShake(false), 500);
            return;
        }

        dispatch(storeCheckCode({ phone, code: otpCode }));
    };

    const handleResendCode = async () => {
        if (!canResend) return;

        setResendLoading(true);
        dispatch(storeReset(phone));
        setResendLoading(false);
        
        // إعادة تعيين التايمر إلى 60 ثانية
        setCountdown(60);
        setCanResend(false);
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0"
            style={{
                minHeight: '100vh',
                background: 'white'
            }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={true}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            {/* النموذج في المنتصف */}
            <div className="col-12 d-flex justify-content-center align-items-center px-4 my-5">
                <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                    style={{
                        width: '100%',
                        maxWidth: '450px',
                        border: 'none',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>

                    <div className="card-body p-5">
                        {/* زر العودة */}
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="btn btn-link p-0 mb-3"
                            onClick={() => navigate(-1)}
                            style={{ color: '#184194' }}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>

                        {/* أيقونة التحقق */}
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: 'spring' }}
                            className="text-center mb-4">
                            <div className={styles.iconContainer}>
                                <FaShieldAlt className={styles.icon} />
                            </div>
                        </motion.div>

                        {/* عنوان التحقق */}
                        <motion.h3
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-center mb-3 fw-bold"
                            style={{ color: '#333' }}>
                            {isForgotPassword ? 'تأكيد الهوية' : isActivation ? 'تفعيل الحساب' : 'التحقق من الكود'}
                        </motion.h3>

                        {/* نص التعليمات */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="text-center mb-4"
                            style={{ 
                                color: '#184194',
                                fontSize: '1rem',
                                fontWeight: '500'
                            }}>
                            تم إرسال كود التحقق إلى رقم الهاتف
                            <br />
                            <span className="fw-bold">{phone}</span>
                        </motion.p>

                        <form onSubmit={handleSubmit}>
                            {/* حقول إدخال الكود */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.6 }}
                                className="mb-4">
                                <div className="d-flex justify-content-center gap-3">
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            id={`otp-${index}`}
                                            type="text"
                                            className={`form-control text-center ${styles.otpInput}`}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(index, e)}
                                            onPaste={handlePaste}
                                            maxLength="1"
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                border: '2px solid #e9ecef',
                                                borderRadius: '10px'
                                            }}
                                        />
                                    ))}
                                </div>
                            </motion.div>

                            {/* زر التحقق */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="btn w-100 text-white py-3 fw-medium"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1.1rem',
                                    marginTop: '1rem'
                                }}
                                disabled={loading}>
                                {loading ? <LoadingSpinner /> : 'تحقق من الكود'}
                            </motion.button>
                        </form>

                        {/* إعادة إرسال الكود */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            className="mt-4 text-center">
                            {canResend ? (
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none"
                                    onClick={handleResendCode}
                                    disabled={resendLoading}
                                    style={{ color: '#184194', fontSize: '0.9rem' }}>
                                    {resendLoading ? <LoadingSpinner /> : 'إعادة إرسال الكود'}
                                </button>
                            ) : (
                                <p className="text-muted mb-0">
                                    يمكنك إعادة الإرسال خلال {countdown} ثانية
                                </p>
                            )}
                        </motion.div>
                    </div>
                </motion.div>
            </div>

        </div>
    );
};

export default StoreOTP;
